'use client';

import { useTranslations } from 'next-intl';

import EyeSmallIcon from '@/assets/icons/eye-small.svg';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';

interface I_WinningSubmissionFormatted {
  price: number;
  entryTime: string;
  transactionHash: string;
}

export interface I_Winner {
  rank: number;
  name: string;
  predicted: string;
  won: string;
  entryTime?: string;
  userAddress?: string;
  avatar?: {
    url: string;
    alt: string;
  };
  winningSubmissions: I_WinningSubmissionFormatted[];
  winningPricesWon: number;
}

interface I_WinnerCardProps {
  winners: I_Winner[];
  connectedAddress?: string;
  onSelectFlashWinner: (winner: I_Winner) => void;
  decimals?: number;
}

function WinnerCard({
  winners,
  connectedAddress,
  onSelectFlashWinner,
  decimals = 2
}: I_WinnerCardProps) {
  const t = useTranslations();

  return (
    <div className="overflow-y-auto">
      {winners.map((winner, index) => {
        const isUserEntry =
          connectedAddress &&
          winner.userAddress &&
          winner.userAddress.toLowerCase() === connectedAddress.toLowerCase();

        const isWinningPrice = winner.winningSubmissions.length > 1;

        return (
          <div
            className={cn(
              'w-full p-4 flex justify-start items-center gap-2.5 outline-1 outline-offset-(--outline-offset-minus) transition-colors duration-[0.4s] cursor-pointer',
              isUserEntry ? 'outline-primary' : 'outline-base-border/20',
              index === 0 && 'rounded-tl-10 rounded-tr-10',
              index === winners.length - 1 && 'rounded-bl-10 rounded-br-10'
            )}
            key={winner.rank}
          >
            <div className="flex-1 flex flex-col items-start">
              <P className="text-foreground text-sm font-semibold leading-5">
                {winner.name}
              </P>
              {isWinningPrice ? (
                <P className="text-foreground text-xs font-light leading-4 whitespace-nowrap">
                  {winner.winningSubmissions.length}{' '}
                  {t('profile.predictionWinners.predictions')}
                </P>
              ) : (
                <P className="text-foreground text-xs font-light leading-4 whitespace-nowrap">
                  {t('profile.predictionWinners.predicted')}{' '}
                  {formatNumberWithCommasAndDecimals(
                    winner.winningSubmissions[0].price,
                    decimals
                  )}
                </P>
              )}
              {/* Todo */}
              {/* <P className="text-foreground text-xs font-light leading-4 whitespace-nowrap">
                {t('profile.predictionWinners.predicted')} {winner.predicted}
              </P> */}
            </div>

            <div className="flex-1 flex justify-center items-center gap-2.5">
              <P className="text-center text-primary text-sm font-bold leading-5">
                {t('profile.predictionWinners.won')}
                <br />
                {winner.won}
              </P>
            </div>

            <div className="flex-1 flex justify-end items-center gap-2.5">
              {isWinningPrice && (
                <Button
                  variant="outline"
                  size="icon"
                  className="w-4 h-4"
                  icon={<EyeSmallIcon className="text-foreground" />}
                  onClick={() => onSelectFlashWinner(winner)}
                />
              )}

              {winner.entryTime && !isWinningPrice && (
                <P className="max-sm:min-w-20 min-w-30 justify-end flex-1 text-right text-muted-foreground text-sm font-normal leading-5">
                  {t('profile.predictionWinners.entryTime')} <br />{' '}
                  {winner.entryTime}
                </P>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default WinnerCard;
